/**
 * Print-only impound release receipt.
 *
 * mode="paid"  → Customer + Office copies on separate A4 sheets (page-break).
 * mode="nts"   → Single internal "Released to Nelson Tow & Salvage" record.
 *
 * Field contract (per user spec):
 *   • Impound Notice Number
 *   • Tow Authority Number
 *   • Driver Full Name / Full Address / Telephone
 *   • Date of Offence/Impound · Time of Seizure
 *   • Impound Location
 *   • Vehicle Make+Model · Registration
 *   • Full fee breakdown + total
 *   • Payment block (paid mode) OR Write-off block (nts mode)
 */
import { tenantLocale, tenantTz, useBranding } from "../lib/company";
import { lastFreeDayIso } from "../lib/tenantTime";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(tenantLocale(), { timeZone: tenantTz(), day: "2-digit", month: "short", year: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(tenantLocale(), { timeZone: tenantTz(), hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" });
}
function fmtMoney(n) {
  return `$${Number(n ?? 0).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
const PM_LABELS = { cash: "Cash", eftpos: "EFTPOS", credit_card: "Credit Card", bank_transfer: "Bank Transfer", other: "Other" };

export default function PrintReceipt({ job, mode = "paid" }) {
  const branding = useBranding();
  const salvageShort = branding?.salvage_entity_name || "Salvage";
  if (mode === "nts") {
    return (
      <div className="print-only">
        <ReceiptCopy job={job} label={`${salvageShort} OFFICE COPY`} mode="nts" pageBreak={false} />
      </div>
    );
  }
  return (
    <div className="print-only">
      <ReceiptCopy job={job} label="CUSTOMER COPY" mode="paid" pageBreak={true} />
      <ReceiptCopy job={job} label="OFFICE COPY" mode="paid" pageBreak={false} />
    </div>
  );
}

function ReceiptCopy({ job, label, mode, pageBreak }) {
  const b = job.pricing_breakdown || {};
  const li = b.line_items || {};
  const noticeNum = job.impound_notice_number || job.police_reference || "—";
  const towAuth = job.tow_authority_number || "—";
  const driverName = job.customer_name || job.impound_owner_name || "—";
  const driverAddr = job.impound_owner_address || "—";
  const driverPhone = job.customer_phone || job.impound_owner_phone || "—";
  const impoundLoc = job.address || "—";
  const isNts = mode === "nts";
  // Oct 2026 — this record now also prints for sign-overs of ordinary tow
  // jobs (AA / ServiceNow / manual), which have no police paperwork, no
  // impound term and no fee engine. Printing "Police Impound" and a row of
  // $0.00 impound fees on those was misleading.
  const isImpound = job.source === "impound" || !!job.impounded_at;
  const branding = useBranding();
  const salvageShort = branding?.salvage_entity_name || "Salvage";
  const salvageFull = branding?.salvage_entity_full || "the towing company";

  return (
    <div className={`receipt-copy${pageBreak ? " receipt-copy-break" : ""}`}>
      <div className="receipt-header">
        <div>
          {/* A tenant that never filled in receipt_brand printed a receipt
              with no company name on it at all. */}
          <div className="receipt-brand">{branding?.receipt_brand || branding?.trading_name || branding?.business_name || ""}</div>
          <div className="receipt-sub">
            {isImpound ? "Police Impound — " : ""}{isNts ? `Release to ${salvageShort} (Write-Off)` : "Release Receipt"}
          </div>
        </div>
        <div className={`receipt-copy-label${isNts ? " receipt-copy-label-nts" : ""}`}>{label}</div>
      </div>

      {/* Police paperwork identifiers — top priority per spec */}
      <table className="receipt-grid">
        <tbody>
          {isImpound && (
            <tr>
              <th>Impound Notice #</th><td className="mono">{noticeNum}</td>
              <th>Tow Authority #</th><td className="mono">{towAuth}</td>
            </tr>
          )}
          <tr>
            <th>Receipt #</th><td className="mono">{(job.id || "").slice(0, 8).toUpperCase()}</td>
            <th>{isNts ? "Date Written Off" : "Date Released"}</th>
            <td>{fmtDateTime(isNts ? job.released_to_nts_at : job.released_at)}</td>
          </tr>
        </tbody>
      </table>

      {/* Driver block */}
      <div className="receipt-section">
        <div className="receipt-section-title">Driver</div>
        <table className="receipt-grid receipt-grid-tight">
          <tbody>
            <tr><th>Full Name</th><td colSpan={3}>{driverName}</td></tr>
            <tr><th>Full Address</th><td colSpan={3}>{driverAddr}</td></tr>
            <tr><th>Telephone</th><td>{driverPhone}</td><th></th><td></td></tr>
          </tbody>
        </table>
      </div>

      {/* Vehicle + Impound details */}
      <div className="receipt-section">
        <div className="receipt-section-title">Vehicle{isImpound ? " & Impound" : ""}</div>
        <table className="receipt-grid receipt-grid-tight">
          <tbody>
            <tr>
              <th>Make &amp; Model</th><td>{job.vehicle || "—"}</td>
              <th>Registration</th><td className="mono">{job.rego || "—"}</td>
            </tr>
            {isImpound ? (
              <>
                <tr>
                  <th>Date of Offence/Impound</th><td>{fmtDate(job.impounded_at)}</td>
                  <th>Time of Seizure</th><td>{fmtTime(job.impounded_at)}</td>
                </tr>
                <tr>
                  <th>Impound Location</th><td colSpan={3}>{impoundLoc}</td>
                </tr>
                <tr>
                  <th>Term</th><td>{job.impound_term === "28d" ? "28 days" : job.impound_term === "6mo" ? "6 months" : "—"}</td>
                  <th>Release Due</th><td>{job.impound_release_due_at ? `${fmtDate(lastFreeDayIso(job.impound_release_due_at))} (end of day)` : "—"}</td>
                </tr>
              </>
            ) : (
              <tr>
                <th>Job Date</th><td>{fmtDate(job.created_at)}</td>
                <th>Location</th><td>{impoundLoc}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Fee breakdown */}
      <table className="receipt-fees">
        <thead><tr><th>Charge</th><th className="right">Amount</th></tr></thead>
        <tbody>
          {isImpound ? (
            <>
              <tr><td>Tow Fee{b.explanations?.tow_fee ? ` — ${b.explanations.tow_fee}` : ""}</td><td className="right mono">{fmtMoney(li.tow_fee)}</td></tr>
              <tr><td>Impound Fee{b.explanations?.impound_fee ? ` — ${b.explanations.impound_fee}` : ""}</td><td className="right mono">{fmtMoney(li.impound_fee)}</td></tr>
              <tr><td>Additional Km</td><td className="right mono">{fmtMoney(li.additional_km_fee)}</td></tr>
              <tr><td>Overstay ({job.days_overstay ?? 0} day{job.days_overstay === 1 ? "" : "s"})</td><td className="right mono">{fmtMoney(li.overstay_fee)}</td></tr>
            </>
          ) : (
            <tr><td>Tow &amp; storage owed</td><td className="right mono">{fmtMoney(b.total)}</td></tr>
          )}
          <tr className="total">
            <td>{isNts ? "TOTAL OWED (WRITTEN OFF)" : "TOTAL"}</td>
            <td className="right mono">{fmtMoney(b.total)}</td>
          </tr>
          {!isNts && (job.part_payments || []).length > 0 && (
            <>
              {job.part_payments.map((p) => (
                <tr key={p.id}>
                  <td>Part payment {fmtDate(p.received_at)} ({PM_LABELS[p.method] || p.method}) — {p.receipt_no}</td>
                  <td className="right mono">−{fmtMoney(p.amount)}</td>
                </tr>
              ))}
              <tr className="total">
                <td>BALANCE PAID ON RELEASE</td>
                <td className="right mono">{fmtMoney(job.payment_amount)}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>

      {/* Payment / NTS write-off block */}
      {!isNts ? (
        <table className="receipt-grid receipt-payment">
          <tbody>
            <tr>
              <th>Payment Method</th><td>{PM_LABELS[job.payment_method] || job.payment_method || "—"}</td>
              <th>Amount Paid</th><td className="mono">{fmtMoney(job.payment_amount)}</td>
            </tr>
            <tr><th>Received By</th><td colSpan={3}>{job.payment_received_by || "—"} · {fmtDateTime(job.payment_received_at)}</td></tr>
          </tbody>
        </table>
      ) : (
        <table className="receipt-grid receipt-payment receipt-payment-nts">
          <tbody>
            <tr>
              <th>Authorised By</th><td>{job.released_to_nts_by || "—"}</td>
              <th>Date</th><td>{fmtDateTime(job.released_to_nts_at)}</td>
            </tr>
            <tr>
              <th>Reason</th>
              <td colSpan={3}>
                {isImpound
                  ? `Driver did not collect within term — vehicle ownership transferred to ${salvageFull} in lieu of payment.`
                  : `Unpaid tow and storage — vehicle ownership transferred to ${salvageFull} in lieu of payment.`}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {job.release_notes && (
        <div className="receipt-section">
          <div className="receipt-section-title">Notes</div>
          <div>{job.release_notes}</div>
        </div>
      )}

      <div className="receipt-footer">
        {isNts ? (
          <>This vehicle is now the property of {salvageFull}. The amount above is recorded as a write-off.</>
        ) : (
          <>
            Vehicle released to owner. Owner acknowledges full payment by signature below.
            <div className="receipt-sig">
              <span>Driver signature: ____________________</span>
              <span>Date: ____________</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
